import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import CheckIcon from '@mui/icons-material/Check';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { SavedMessage } from '@features/savedMessages';
import { useCreateTemplateSavedMessage } from '../hooks/useCreateTemplateSavedMessage';
import { useUpdateTemplateSavedMessage } from '../hooks/useUpdateTemplateSavedMessage';
import BundledEditor from '@utils/BundledEditor';
import { Close, HelpOutline } from '@mui/icons-material';
import { SavedMessagesIcon } from '../../../vectors/index';
import { getStrippedText } from '@utils/helper';
import AIPromptModal from '@features/messaging/components/AIPromptModal/AIPromptModal';
import AssistantIcon from '@mui/icons-material/Assistant';
import { AITooltipText } from '@features/messaging';

type TemplateSavedMessageModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  initialMessage?: string;
  message?: SavedMessage | null;
  templateId: number;
};

type FormData = {
  label: string;
  content: string;
};

const schema: yup.ObjectSchema<FormData> = yup
  .object({
    label: yup
      .string()
      .required('Label is required')
      .test('not-empty-html', 'Label cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    content: yup
      .string()
      .required('Message is required')
      .test('not-empty-html', 'Message cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
  })
  .required();

const TemplateSavedMessageModal: FC<TemplateSavedMessageModalProps> = ({
  isOpen,
  handleClose,
  initialMessage = '',
  message,
  templateId,
}) => {
  const theme = useTheme();
  const [errorText, setErrorText] = useState('');
  const [showAiPromptModal, setShowAiPromptModal] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      label: '',
      content: initialMessage ?? '',
    },
  });

  const handleInsertAIContent = (content: string) => {
    setValue('content', content);
    setShowAiPromptModal(false);
  };

  const { createSavedMessage, isCreatingSavedMessage } =
    useCreateTemplateSavedMessage({
      setErrorText,
    });

  const { updateSavedMessage, isUpdatingSavedMessage } =
    useUpdateTemplateSavedMessage({
      setErrorText,
    });

  const onSubmit = (values: FormData) => {
    const options = {
      onSuccess: () => {
        handleClose();
        reset();
      },
    };

    return message
      ? updateSavedMessage(
          { ...values, etemplate_id: templateId, saved_message_id: message.id },
          options,
        )
      : createSavedMessage({ ...values, etemplate_id: templateId }, options);
  };

  useEffect(() => {
    if (isOpen && initialMessage) {
      setValue('content', initialMessage);
    }

    if (isOpen && message) {
      setValue('label', message.label);
      setValue('content', message.content);
    }
  }, [isOpen, initialMessage, message]);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => {
          handleClose();
          reset();
        }}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <Close />
          </IconButton>
          <br />
          <SavedMessagesIcon />
          <br />
          <Typography variant="h4" mt={2}>
            {message ? 'Edit Saved Message' : 'Add Saved Message'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <form
            onSubmit={handleSubmit(onSubmit)}
            css={css`
              p {
                margin-left: ${theme.spacing(1)};
                font-size: 15px;
              }
            `}
            style={{ marginBottom: '10px' }}
          >
            <Stack spacing={2}>
              {errorText && <Alert severity="error">{errorText}</Alert>}
              <Typography>Label:</Typography>
              <Controller
                control={control}
                name="label"
                render={({ field }) => (
                  <TextField
                    {...field}
                    hiddenLabel
                    placeholder="Label"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.label)}
                    helperText={errors?.label?.message ?? ''}
                  />
                )}
              />
              <Typography>Message:</Typography>
              <Controller
                control={control}
                name="content"
                render={({ field }) => (
                  <Stack spacing={1}>
                    <BundledEditor
                      {...field}
                      onEditorChange={(newValue: string) =>
                        field.onChange(newValue)
                      }
                      onChange={() => {}}
                      init={{
                        height: 200,
                        menubar: false,
                        plugins: ['lists', 'autolink', 'link', 'pagebreak'],
                        toolbar:
                          'spellchecker language spellcheckdialog | styles | bold italic | bullist numlist | link',
                        spellchecker_dialog: true,
                        spellchecker_rpc_url: 'localhost/ephox-spelling',
                        spellchecker_language: 'en_US',
                        spellchecker_active: true,
                        browser_spellcheck: true,
                        contextmenu: false,
                      }}
                    />
                    {Boolean(errors?.content?.message) && (
                      <Typography color="error">
                        {errors?.content?.message}
                      </Typography>
                    )}
                    <Stack mt={1} spacing={2} direction="row" alignItems="center" justifyContent='end'>
                      <Button
                        variant="outlined"
                        size='small'
                        onClick={() => setShowAiPromptModal(true)}
                        startIcon={<AssistantIcon />}
                      >
                        Write with AI
                      </Button>
                      <Tooltip title={AITooltipText} placement="top" arrow>
                        <HelpOutline color="secondary" fontSize="small" />
                      </Tooltip>
                    </Stack>
                  </Stack>
                )}
              />
              <Button
                type="submit"
                variant="contained"
                size="small"
                fullWidth
                disabled={isCreatingSavedMessage || isUpdatingSavedMessage}
                color="secondary"
                style={{ marginTop: '20px' }}
                startIcon={<CheckIcon />}
              >
                Save
              </Button>
            </Stack>
          </form>
        </DialogContent>
      </Dialog>
      {showAiPromptModal && (
        <AIPromptModal
          isOpen={showAiPromptModal}
          handleInsertAIContent={handleInsertAIContent}
          handleClose={() => setShowAiPromptModal(false)}
        />
      )}
    </>
  );
};

export default TemplateSavedMessageModal;
